import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import AuthModal from '../Modals/AuthModal';

const { FiFileText, FiShield, FiArrowRight, FiLock, FiExternalLink, FiX, FiCheckCircle, FiUser } = FiIcons;

const EstateManagementWidget = ({ memorial }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [modal, setModal] = useState({ isOpen: false, doc: '', requested: false, type: 'document' });
  const [isAuthOpen, setIsAuthOpen] = useState(false);

  const docs = [
    { id: 'will', name: 'Last Will & Testament', date: 'Oct 2023' },
    { id: 'trust', name: 'Living Trust', date: 'Nov 2023' }
  ];

  const handleRequest = () => {
    setModal(m => ({ ...m, requested: true }));
    setTimeout(() => setModal({ isOpen: false, doc: '', requested: false, type: 'document' }), 2000);
  };

  const handleEnroll = () => {
    if (!user) {
      // Show restricted access modal if not logged in
      setModal({ 
        isOpen: true, 
        doc: 'Digital Heir Service', 
        requested: false, 
        type: 'enroll' 
      });
      return;
    }

    // Navigate to the Admin / Legacy tab on the same page
    navigate(`${location.pathname}?tab=legacy`);
    
    // On mobile, scroll to the tab section to ensure the user sees the change
    if (window.innerWidth < 1024) {
      const tabSection = document.querySelector('.hide-scrollbar');
      if (tabSection) {
        tabSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 600, behavior: 'smooth' });
      }
    }
  };

  const closeMainModal = () => {
    setModal({ isOpen: false, doc: '', requested: false, type: 'document' });
  };

  return (
    <div className="bg-white rounded-[32px] border border-brand-dark shadow-sm overflow-hidden p-6 space-y-6">
      <div className="flex items-center gap-3 border-b border-brand-light pb-4">
        <div className="w-10 h-10 rounded-xl bg-accent-navy/5 flex items-center justify-center text-accent-navy">
          <SafeIcon icon={FiShield} />
        </div>
        <div>
          <h3 className="text-xs font-black uppercase tracking-widest text-accent-navy">Estate Vault</h3>
          <p className="text-[10px] text-text-muted">Secure encryption active</p>
        </div>
      </div>

      <div className="space-y-3">
        {docs.map(doc => (
          <div 
            key={doc.id} 
            onClick={() => setModal({ isOpen: true, doc: doc.name, requested: false, type: 'document' })} 
            className="flex items-center justify-between p-3 bg-brand-light rounded-2xl cursor-pointer hover:border-accent-gold border-2 border-transparent transition-all"
          >
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiFileText} className="text-accent-navy" />
              <div>
                <p className="text-xs font-bold text-accent-navy">{doc.name}</p>
                <p className="text-[9px] text-text-muted">Locked • {doc.date}</p>
              </div>
            </div>
            <SafeIcon icon={FiLock} className="w-4 h-4 text-amber-500" />
          </div>
        ))}
      </div>

      <div className="bg-accent-navy p-5 rounded-3xl text-white">
        <h4 className="font-serif text-lg mb-2">Digital Heir</h4>
        <p className="text-[10px] opacity-70 mb-4 leading-relaxed">
          Executors gain access to the asset inventory, guided checklists for contacting banks/agencies, and logging actions for an audit trail.
        </p>
        <button 
          onClick={handleEnroll}
          className="w-full btn-accent py-3 text-[10px] font-black uppercase"
        >
          Enroll in Service
        </button>
      </div>

      <AnimatePresence>
        {modal.isOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }} 
              animate={{ opacity: 1, scale: 1 }} 
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white p-8 rounded-[32px] max-w-sm w-full text-center relative shadow-2xl border border-brand-dark"
            >
              <button 
                onClick={closeMainModal}
                className="absolute top-6 right-6 p-2 bg-brand-light text-text-muted rounded-full hover:bg-brand-dark transition-colors"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>

              <div className="w-16 h-16 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon 
                  icon={modal.requested ? FiCheckCircle : (modal.type === 'enroll' ? FiUser : FiLock)} 
                  className={`w-8 h-8 ${modal.requested ? 'text-green-500' : 'text-accent-gold'}`} 
                />
              </div>
              
              <h3 className="text-xl font-serif text-accent-navy mb-2">
                {modal.requested ? 'Request Sent' : (modal.type === 'enroll' ? 'Sign In Required' : 'Restricted Access')}
              </h3>
              
              <p className="text-sm text-text-muted mb-6">
                {modal.type === 'enroll' 
                  ? `You must be signed in to your sanctuary account to enroll in the ${modal.doc}.`
                  : `Permission required to view ${modal.doc}.`
                }
              </p>

              {!modal.requested ? (
                <div className="space-y-3">
                  {modal.type === 'enroll' ? (
                    <button 
                      onClick={() => {
                        closeMainModal();
                        setIsAuthOpen(true);
                      }} 
                      className="w-full btn-primary py-3 flex items-center justify-center gap-2"
                    >
                      <SafeIcon icon={FiUser} /> Sign In to Proceed
                    </button>
                  ) : (
                    <button onClick={handleRequest} className="w-full btn-primary py-3">Request Access</button>
                  )}
                  <button 
                    onClick={closeMainModal}
                    className="w-full py-3 text-xs font-black uppercase tracking-widest text-text-muted hover:text-accent-navy transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <p className="text-xs text-green-600 font-bold">Your request has been logged and sent to the administrator.</p>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AuthModal isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} />
    </div>
  );
};

export default EstateManagementWidget;